/**
 * Inspections (ADR-63 Cut 5, design section 26). A reusable checklist (a
 * template) is run against a kit or an asset, producing a result. The result
 * is COMPUTED from the item outcomes, never typed by hand, so "READY" cannot be
 * claimed while a required item failed.
 *
 * The one readiness rule this adds (design validation rule 5): a kit whose
 * latest inspection FAILED cannot be READY - it reads NEEDS_INSPECTION, the
 * state Cut 2 reserved and Cut 4 left for this cut to fill.
 */

export const INSPECTION_RESULTS = ['READY', 'READY_WITH_WARNING', 'INCOMPLETE', 'FAILED'] as const;
export type InspectionResult = (typeof INSPECTION_RESULTS)[number];

export const INSPECTION_RESULT_LABEL: Readonly<Record<InspectionResult, string>> = {
  READY: 'Ready',
  READY_WITH_WARNING: 'Ready with warning',
  INCOMPLETE: 'Incomplete',
  FAILED: 'Failed',
};

export const INSPECTION_ITEM_OUTCOMES = ['PASS', 'WARN', 'FAIL', 'NA'] as const;
export type InspectionItemOutcome = (typeof INSPECTION_ITEM_OUTCOMES)[number];

export const INSPECTION_ITEM_OUTCOME_LABEL: Readonly<Record<InspectionItemOutcome, string>> = {
  PASS: 'Pass',
  WARN: 'Warning',
  FAIL: 'Fail',
  NA: 'N/A',
};

/** One line of a run, as `computeInspectionResult` reasons over it. */
export interface InspectionLine {
  readonly required: boolean;
  /** null = not recorded yet. */
  readonly outcome: InspectionItemOutcome | null;
}

/**
 * The overall result from the lines, worst-first: any required line still
 * unrecorded makes the run INCOMPLETE; any required FAIL makes it FAILED; any
 * WARN (required or not) makes it READY_WITH_WARNING; otherwise READY. A NA or
 * a non-required unrecorded line does not hold the run back.
 */
export function computeInspectionResult(lines: readonly InspectionLine[]): InspectionResult {
  const requiredFailed = lines.some((l) => l.required && l.outcome === 'FAIL');
  if (requiredFailed) return 'FAILED';
  const requiredUnrecorded = lines.some((l) => l.required && l.outcome === null);
  if (requiredUnrecorded) return 'INCOMPLETE';
  const anyWarn = lines.some((l) => l.outcome === 'WARN');
  return anyWarn ? 'READY_WITH_WARNING' : 'READY';
}

/** A latest-inspection result that blocks kit readiness (design rule 5). */
export function inspectionBlocksReadiness(result: InspectionResult): boolean {
  return result === 'FAILED';
}

export function isInspectionResult(v: unknown): v is InspectionResult {
  return typeof v === 'string' && (INSPECTION_RESULTS as readonly string[]).includes(v);
}
